import React from 'react';
import PropTypes from 'prop-types';
import { Flex, Box, Text } from 'rebass/styled-components';
import { Button } from '@getro/rombo';
import { Plus } from 'lucide-react';
import { AdminListItem } from '../adminListItem';
import BlankMessage from '../BlankMessage';

export const AdminList = ({
  onEmptyListClick,
  users,
  onResendInvitation,
  isResendingInvitation,
  showEmailAddress,
  onRemoveAdmin,
  invitedUsers,
}) => {
  // Ensure both arrays are defined before proceeding
  const activeUsers = users || [];
  const pendingUsers = invitedUsers || [];

  if (!activeUsers.length && !pendingUsers.length) {
    const children = (
      <Text data-testid="no-talent-network-active">
        Invite founders, hiring managers, or sales leads to create their own Getro network and manage their company
        profile.{' '}
      </Text>
    );
    return (
      <BlankMessage title="This company hasn't joined Getro yet" buttonText="Invite admin" onClick={onEmptyListClick}>
        {children}
      </BlankMessage>
    );
  }
  return (
    <Flex data-testid="admin-list" sx={{ width: '100%', maxWidth: '800px' }}>
      <Box width={1}>
        <Flex justifyContent="flex-end">
          <Button
            data-testid="invite-admin-button"
            size="small"
            variant="secondary"
            onClick={onEmptyListClick}
            icon={Plus}
            iconGap="8px"
          >
            Invite admin
          </Button>
        </Flex>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            textTransform: 'uppercase',
            fontSize: '12px',
            color: 'text.subtle',
            height: '32px',
            mb: '8px',
            borderBottom: '1px solid',
            borderColor: 'neutral.100',
            letterSpacing: '1px',
          }}
        >
          <Box width={1 / 2}>Admin</Box>
          <Box width={1 / 2}>Invitation status</Box>
        </Box>
        <Flex
          sx={{
            flexDirection: 'column',
            gap: '8px',
          }}
        >
          {activeUsers.map((user) => (
            <AdminListItem
              key={`active-${user.id}`}
              user={user}
              onResendInvitation={onResendInvitation}
              isResendingInvitation={isResendingInvitation}
              showEmailAddress={showEmailAddress}
              onRemoveAdmin={onRemoveAdmin}
              sx={{
                height: '56px',
              }}
            />
          ))}
          {pendingUsers.map((user) => (
            <AdminListItem
              key={`invited-${user.id}`}
              user={user}
              onResendInvitation={onResendInvitation}
              isResendingInvitation={isResendingInvitation}
              showEmailAddress={showEmailAddress}
              onRemoveAdmin={onRemoveAdmin}
              hasAcceptedInvitation={false}
              sx={{
                height: '56px',
              }}
            />
          ))}
        </Flex>
      </Box>
    </Flex>
  );
};

AdminList.propTypes = {
  users: PropTypes.array,
  onResendInvitation: PropTypes.func,
  isResendingInvitation: PropTypes.bool,
  onRemoveAdmin: PropTypes.func,
  onEmptyListClick: PropTypes.func,
  showEmailAddress: PropTypes.bool.isRequired,
  invitedUsers: PropTypes.array,
};

AdminList.defaultProps = {
  users: null,
  onResendInvitation: null,
  onEmptyListClick: () => {},
  onRemoveAdmin: null,
  isResendingInvitation: false,
  invitedUsers: [],
};
